// Browser-side translator client. Takes serialized HTTP requests
// (method / path / headers / streaming body) and maps them to wire
// frames on an ordered byte stream (WebRTC data channel or test
// harness). Returns RES / DATA / END streams as a response
// descriptor + a body stream.
//
// This mirrors the design of `crates/translator/src/client.rs` but
// adapts it to the browser: no tokio, no tasks — the transport is
// an async byte sink/source, and per-stream buffering is built on
// `ReadableStream`.

import {
  decode,
  encode,
  ErrorCode,
  FLAG_END_STREAM,
  type Frame,
  type Headers,
} from "../wire/index.js";
import { debugLog, isVerbose } from "../debug-log.js";

/** The byte-level transport we ride on. */
export interface ByteTransport {
  /** Send a frame of bytes. Backpressure is handled by the promise. */
  send(bytes: Uint8Array): void | Promise<void>;
  /**
   * Subscribe to inbound bytes. The callback is invoked with every
   * chunk the transport receives; ordering must match send order.
   * Returns an unsubscribe function.
   */
  onMessage(listener: (chunk: Uint8Array) => void): () => void;
  /** Subscribe to transport-close events. */
  onClose(listener: () => void): () => void;
}

/** Outgoing request descriptor — what the SW hands over the port. */
export interface TranslatorRequest {
  method: string;
  path: string;
  headers: Array<readonly [string, string]>;
  /** `null` for bodyless requests (GET, HEAD, etc.). */
  body: ReadableStream<Uint8Array> | null;
}

export interface TranslatorResponse {
  status: number;
  headers: Array<readonly [string, string]>;
  body: ReadableStream<Uint8Array>;
}

/**
 * Thin per-connection translator. Multiplexes requests onto the
 * single transport and fan-outs response frames to the correct
 * pending stream.
 */
/**
 * Per-frame-type rolling totals plus aggregate byte count emitted by
 * `TranslatorClient.rxSummary()`. Used by upstream consumers
 * (the SW handoff's stall watchdog) to attach a "what arrived on the
 * data channel before we gave up" report to the diagnostic log.
 *
 * `orphanRes` / `orphanData` count frames whose `streamId` did not
 * match any entry in `pending` at dispatch time — the canonical
 * stream-id-mismatch failure mode the report exists to surface.
 */
export interface TranslatorRxSummary {
  res: number;
  data: number;
  end: number;
  err: number;
  ws_accept: number;
  ws_msg: number;
  ws_close: number;
  other: number;
  bytes: number;
  /** `res` frames whose stream id had no matching pending request.
   *  Should always be zero — non-zero means the box opened a stream
   *  we never initiated. */
  orphanRes: number;
  /** `data` frames whose stream id was either unknown OR known but
   *  had no body controller yet (data arrived before res). Non-zero
   *  here is the smoking gun the watchdog log is hunting for. */
  orphanData: number;
}

/**
 * Conservative default cap on the body bytes packed into one DATA
 * frame. Sized to fit comfortably below any browser's
 * `RTCDataChannel.maxMessageSize` after the wire-header overhead
 * (length+type+flags+streamId = 10 bytes). Chrome currently advertises
 * `maxMessageSize` around 64 KiB; Firefox 65 KiB; Safari smaller. 16
 * KiB is well below all of them and produces enough chunks to keep
 * back-pressure responsive without spamming the wire.
 *
 * If a future revision reads the negotiated cap from signaling or
 * derives it from `dc.maxMessageSize` per-session, this becomes the
 * fallback when neither signal is available.
 */
export const DEFAULT_MAX_FRAME_BODY_BYTES = 16 * 1024;

/**
 * Default cap on bytes buffered in a single inbound stream's
 * `ReadableStream` queue waiting for the page consumer to read.
 *
 * Web Streams' default backpressure doesn't propagate to the
 * DataChannel — `enqueue()` is non-blocking and there is no `pull`
 * hook to throttle the producer. So a page that opens a streaming
 * response and never reads it would accumulate bytes here until
 * the tab OOMs. The cap fires before that happens: the stream is
 * errored locally and a `Frame::Err{CANCEL}` is sent to the box
 * so it stops producing.
 *
 * 16 MiB is large enough not to false-positive on normal SSE
 * bursts or chunked downloads being read at a normal rate, and
 * small enough that a runaway producer trips the cap well before
 * tab-OOM territory.
 */
export const DEFAULT_MAX_STREAM_BUFFER_BYTES = 16 * 1024 * 1024;

export interface TranslatorClientOptions {
  /** Per-DATA-frame body cap (in bytes). See
   *  {@link DEFAULT_MAX_FRAME_BODY_BYTES}. */
  maxFrameBodyBytes?: number;
  /** Per-inbound-stream buffer cap (in bytes). See
   *  {@link DEFAULT_MAX_STREAM_BUFFER_BYTES}. */
  maxStreamBufferBytes?: number;
}

export class TranslatorClient {
  private nextStreamId = 1;
  private readonly pending = new Map<number, Pending>();
  private readonly pendingInbound: Uint8Array[] = [];
  private pendingLen = 0;
  private unsubscribe: (() => void) | null = null;
  private unsubscribeClose: (() => void) | null = null;
  private closed = false;
  private readonly maxFrameBodyBytes: number;
  private readonly maxStreamBufferBytes: number;
  /**  per-type RX counters. Mutated on every `dispatch()` call;
   *  read back by `rxSummary()`. Cheap (integer adds) so we keep them
   *  always-on rather than gating on a debug flag. */
  private readonly rx: TranslatorRxSummary = {
    res: 0,
    data: 0,
    end: 0,
    err: 0,
    ws_accept: 0,
    ws_msg: 0,
    ws_close: 0,
    other: 0,
    bytes: 0,
    orphanRes: 0,
    orphanData: 0,
  };

  constructor(
    private readonly transport: ByteTransport,
    options: TranslatorClientOptions = {},
  ) {
    const cap = options.maxFrameBodyBytes ?? DEFAULT_MAX_FRAME_BODY_BYTES;
    if (!Number.isFinite(cap) || cap <= 0) {
      throw new Error(
        `TranslatorClient: maxFrameBodyBytes must be a positive number, got ${cap}`,
      );
    }
    this.maxFrameBodyBytes = cap;
    const bufCap =
      options.maxStreamBufferBytes ?? DEFAULT_MAX_STREAM_BUFFER_BYTES;
    if (!Number.isFinite(bufCap) || bufCap <= 0) {
      throw new Error(
        `TranslatorClient: maxStreamBufferBytes must be a positive number, got ${bufCap}`,
      );
    }
    this.maxStreamBufferBytes = bufCap;
    this.unsubscribe = transport.onMessage((chunk) => this.onChunk(chunk));
    this.unsubscribeClose = transport.onClose(() => this.onTransportClose());
  }

  /**
   * Snapshot of the per-frame-type RX counters. Returns a
   * shallow copy so callers can format it without worrying about
   * later mutation. Useful at stall-watchdog time to attach "what
   * arrived on the data channel before we gave up."
   */
  rxSummary(): TranslatorRxSummary {
    return { ...this.rx };
  }

  /**
   * Issue a request. Resolves when the RES frame arrives; the body
   * continues to stream on the returned `response.body`.
   */
  async request(req: TranslatorRequest): Promise<TranslatorResponse> {
    if (this.closed) throw new Error("TranslatorClient: transport closed");

    const streamId = this.nextStreamId++;
    const wireHeaders = toWireHeaders(req.headers);

    const pending = makePending();
    this.pending.set(streamId, pending);

    // Send REQ. If there's no body, mark END_STREAM to save a round.
    const hasBody = req.body !== null;
    const reqFrame: Frame = {
      type: "req",
      streamId,
      flags: hasBody ? 0 : FLAG_END_STREAM,
      method: new TextEncoder().encode(req.method),
      path: new TextEncoder().encode(req.path),
      headers: wireHeaders,
    };
    await this.transport.send(encode(reqFrame));

    // Pump the request body as DATA frames.
    if (req.body) {
      // Don't await — we want to let the response start streaming in
      // concurrently with the request body. But propagate errors so
      // the response-side consumer sees them as a failed stream.
      void this.pumpRequestBody(streamId, req.body).catch((err) => {
        pending.rejectResponse(
          err instanceof Error ? err : new Error(String(err)),
        );
      });
    }

    return pending.responsePromise;
  }

  close(): void {
    if (this.closed) return;
    this.closed = true;
    this.unsubscribe?.();
    this.unsubscribeClose?.();
    for (const p of this.pending.values()) {
      p.rejectResponse(new Error("TranslatorClient: closed"));
      p.bodyController?.error(new Error("TranslatorClient: closed"));
    }
    this.pending.clear();
  }

  // ---------- frame I/O ----------

  private onChunk(chunk: Uint8Array): void {
    this.pendingInbound.push(chunk);
    this.pendingLen += chunk.length;
    this.drain();
  }

  private drain(): void {
    // Fast path: a single chunk that contains ≥ 1 whole frame.
    while (this.pendingLen > 0) {
      const buf = this.coalesce();
      const result = tryDecode(buf);
      if (!result) return;
      this.consume(result.consumed);
      this.dispatch(result.frame);
    }
  }

  private coalesce(): Uint8Array {
    if (this.pendingInbound.length === 1) return this.pendingInbound[0]!;
    const out = new Uint8Array(this.pendingLen);
    let p = 0;
    for (const c of this.pendingInbound) {
      out.set(c, p);
      p += c.length;
    }
    // Replace the chunk list with the coalesced buffer so we only pay
    // the copy once per coalesce round.
    this.pendingInbound.length = 0;
    this.pendingInbound.push(out);
    return out;
  }

  private consume(n: number): void {
    // After coalesce() there is exactly one chunk. Slice it.
    const buf = this.pendingInbound[0]!;
    if (n >= buf.length) {
      this.pendingInbound.length = 0;
      this.pendingLen = 0;
      return;
    }
    this.pendingInbound[0] = buf.subarray(n);
    this.pendingLen -= n;
  }

  private dispatch(frame: Frame): void {
    // Per-frame RX log + counter bump. The log only exists when an
    // operator sets `localStorage.p2claw_verbose = "true"` — the
    // string build is guarded on the cached flag so the steady-state
    // per-frame cost is a timestamp compare. The counters bump
    // unconditionally — they're queried by `rxSummary()`.
    //
    // `goaway` / `ping` / `pong` are connection-level (no streamId);
    // they fall into the helper's "?" branch.
    const size = frameBodySize(frame);
    if (isVerbose()) {
      const streamId = frameStreamId(frame);
      const known = streamId !== null && this.pending.has(streamId);
      debugLog(
        `[p2claw:dc] rx kind=${frame.type} stream_id=${streamId ?? "?"} size=${size} known_stream=${known}`,
      );
    }
    this.rx.bytes += size;
    switch (frame.type) {
      case "res": {
        this.rx.res += 1;
        const p = this.pending.get(frame.streamId);
        if (!p) {
          this.rx.orphanRes += 1;
          console.warn(
            `[p2claw:dc] orphan res stream_id=${frame.streamId} status=${frame.status} — no matching pending request`,
          );
          return;
        }
        const { body, bodyController } = createBodyStream(
          this.maxStreamBufferBytes,
        );
        p.bodyController = bodyController;
        p.resolveResponse({
          status: frame.status,
          headers: fromWireHeaders(frame.headers),
          body,
        });
        if ((frame.flags & FLAG_END_STREAM) !== 0) {
          p.bodyController.close();
          this.pending.delete(frame.streamId);
        }
        break;
      }
      case "data": {
        this.rx.data += 1;
        const p = this.pending.get(frame.streamId);
        if (!p?.bodyController) {
          // Either the stream id is unknown OR res hasn't been
          // dispatched yet (so bodyController is still null). Either
          // way the bytes are dropped silently — flag it loudly so
          // a trace surfaces stream-id mismatches and the
          // data-before-res race (both equally diagnostic).
          this.rx.orphanData += 1;
          console.warn(
            `[p2claw:dc] orphan data stream_id=${frame.streamId} size=${frame.body.length} known_pending=${!!p} has_body_controller=${!!p?.bodyController} — chunk dropped`,
          );
          return;
        }
        if (frame.body.length > 0) {
          p.bodyController.enqueue(frame.body);
          // Web Streams doesn't propagate this backpressure to the
          // DataChannel — the producer-side queue grows without
          // bound when the page consumer is slow or absent. Trip
          // when the controller reports we're past the strategy's
          // high-water mark: error the local stream so the page
          // sees a clean fetch failure, and emit a CANCEL so the
          // box stops sending more bytes for this stream.
          if ((p.bodyController.desiredSize ?? 0) < 0) {
            this.cancelOverflowingStream(frame.streamId, p);
            return;
          }
        }
        if ((frame.flags & FLAG_END_STREAM) !== 0) {
          p.bodyController.close();
          this.pending.delete(frame.streamId);
        }
        break;
      }
      case "end": {
        this.rx.end += 1;
        const p = this.pending.get(frame.streamId);
        if (!p) return;
        p.bodyController?.close();
        this.pending.delete(frame.streamId);
        break;
      }
      case "err": {
        this.rx.err += 1;
        const p = this.pending.get(frame.streamId);
        if (!p) return;
        const msg = new TextDecoder().decode(frame.message);
        const e = new Error(
          `box sent ERR code=0x${frame.code.toString(16).padStart(4, "0")}: ${msg}`,
        );
        p.rejectResponse(e);
        p.bodyController?.error(e);
        this.pending.delete(frame.streamId);
        break;
      }
      // WebSocket frames are handled via a separate path; see ws-shim.ts.
      case "ws_accept":
        this.rx.ws_accept += 1;
        // fallthrough to common ws handling
      case "ws_msg":
        if (frame.type === "ws_msg") this.rx.ws_msg += 1;
        // fallthrough
      case "ws_close": {
        if (frame.type === "ws_close") this.rx.ws_close += 1;
        const p = this.pending.get(frame.streamId);
        if (!p?.wsHandler) return;
        p.wsHandler(frame);
        break;
      }
      case "probe": {
        this.rx.other += 1;
        // Runtime chunk-cap probe (paired with the box-agent
        // probe-send side). Synchronously emit a ProbeAck on the same data
        // channel echoing the probe id. No state machine — the box
        // owns the decision window; our job is just to respond
        // fast. `transport.send` may return a Promise (data channel
        // backpressure); we don't await it here because the
        // translator's dispatch must remain synchronous, but we
        // surface the rejection if one occurs.
        const ackFrame: Frame = {
          type: "probe_ack",
          flags: 0,
          id: frame.id,
        };
        const sent = this.transport.send(encode(ackFrame));
        if (sent && typeof (sent as Promise<unknown>).catch === "function") {
          (sent as Promise<unknown>).catch((e) => {
            console.warn("[p2claw:dc] probe_ack send failed:", e);
          });
        }
        break;
      }
      case "probe_ack":
        this.rx.other += 1;
        // The wrapper sends ProbeAck; the box sends Probe. Receiving
        // a ProbeAck means either a misbehaving peer or a future
        // bidirectional extension we don't model. Drop silently —
        // already counted in rx.other.
        break;
      case "unknown":
        this.rx.other += 1;
        debugLog(
          `[p2claw:dc] unknown_frame typeByte=0x${frame.typeByte
            .toString(16)
            .padStart(2, "0")} flags=${frame.flags} payload_size=${frame.payload.length} — skipping`,
        );
        break;
      case "goaway":
      case "ping":
      case "pong":
      case "req":
      case "ws_upgrade":
        this.rx.other += 1;
        // Visitor-initiated frame types the box shouldn't be sending,
        // or connection-level control that this layer does not handle.
        break;
    }
  }

  // ---------- visitor-initiated stream cancel ----------

  /**
   * Tear down a stream whose inbound queue has overflowed the
   * per-stream buffer cap. Errors the local ReadableStream so the
   * page sees a clean fetch failure and sends `Frame::Err{CANCEL}`
   * upstream so the box stops producing more bytes.
   *
   * The receiver removes pending state synchronously. Subsequent
   * DATA frames for the same stream-id race against the upstream
   * cancel reaching the box; they're handled as orphan-data by
   * the existing `case "data":` guard.
   */
  private cancelOverflowingStream(streamId: number, p: Pending): void {
    const reason = new Error(
      `p2claw: per-stream buffer cap of ${this.maxStreamBufferBytes} bytes exceeded on stream ${streamId}`,
    );
    try {
      p.bodyController?.error(reason);
    } catch {
      // Already errored / closed — ignore.
    }
    this.pending.delete(streamId);
    const message = new TextEncoder().encode(
      "visitor: per-stream buffer cap exceeded",
    );
    const cancelFrame: Frame = {
      type: "err",
      streamId,
      code: ErrorCode.CANCEL,
      message,
    };
    const sent = this.transport.send(encode(cancelFrame));
    if (sent && typeof (sent as Promise<void>).then === "function") {
      (sent as Promise<void>).catch((e) => {
        debugLog(
          "[p2claw:dc] cancel-emit failed",
          `stream_id=${streamId}`,
          e,
        );
      });
    }
  }

  // ---------- body pump ----------

  private async pumpRequestBody(
    streamId: number,
    body: ReadableStream<Uint8Array>,
  ): Promise<void> {
    const reader = body.getReader();
    const cap = this.maxFrameBodyBytes;
    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        if (value.length === 0) continue;
        // Slice oversized reads into ≤cap DATA frames. The browser's
        // `RTCDataChannel.send()` rejects a single message larger than
        // the negotiated `maxMessageSize` (typically ~64 KiB) with a
        // synchronous throw that surfaces to the app as "Failed to
        // fetch", so a body chunk that arrives in one read MUST go on
        // the wire as several small DATA frames. The receiver's
        // `case "data":` already reassembles into one ReadableStream
        // — no protocol change.
        for (let offset = 0; offset < value.length; offset += cap) {
          const end = Math.min(offset + cap, value.length);
          const slice =
            offset === 0 && end === value.length
              ? value
              : value.subarray(offset, end);
          await this.transport.send(
            encode({ type: "data", streamId, flags: 0, body: slice }),
          );
        }
      }
      await this.transport.send(
        encode({ type: "data", streamId, flags: FLAG_END_STREAM, body: new Uint8Array(0) }),
      );
    } finally {
      reader.releaseLock();
    }
  }

  // ---------- ws integration ----------

  /**
   * Open a WebSocket stream. The returned handle is consumed by
   * ws-shim.ts; this method is kept here because WS streams share the
   * same transport + stream-ID space as HTTP streams.
   */
  openWebSocket(input: {
    path: string;
    headers: Array<readonly [string, string]>;
    onFrame: (frame: Frame) => void;
  }): { streamId: number; send: (frame: Frame) => void | Promise<void> } {
    const streamId = this.nextStreamId++;
    const pending = makePending();
    pending.wsHandler = input.onFrame;
    this.pending.set(streamId, pending);
    const upgrade: Frame = {
      type: "ws_upgrade",
      streamId,
      path: new TextEncoder().encode(input.path),
      headers: toWireHeaders(input.headers),
    };
    void this.transport.send(encode(upgrade));
    return {
      streamId,
      send: (frame) => this.transport.send(encode(frame)),
    };
  }

  /** Close a WS stream from the visitor side. */
  closeWebSocket(streamId: number): void {
    this.pending.delete(streamId);
  }

  private onTransportClose(): void {
    this.close();
  }
}

// ---------- helpers ----------

/** Best-effort size accessor for the per-frame RX log. Returns
 *  the bytes carried in the frame's payload — body length for data,
 *  message length for err, 0 otherwise. */
function frameBodySize(frame: Frame): number {
  switch (frame.type) {
    case "data":
      return frame.body.length;
    case "err":
      return frame.message.length;
    case "ws_msg":
      return frame.payload?.length ?? 0;
    case "probe":
    case "unknown":
      return frame.payload.length;
    default:
      return 0;
  }
}

/** Stream id for the diagnostic log. Returns null for the
 *  connection-level frames that don't carry one (`goaway`, `ping`,
 *  `pong`, `probe`, `probe_ack`, `unknown`). */
function frameStreamId(frame: Frame): number | null {
  switch (frame.type) {
    case "goaway":
    case "ping":
    case "pong":
    case "probe":
    case "probe_ack":
    case "unknown":
      return null;
    default:
      return frame.streamId;
  }
}

interface Pending {
  responsePromise: Promise<TranslatorResponse>;
  resolveResponse: (r: TranslatorResponse) => void;
  rejectResponse: (e: Error) => void;
  bodyController: ReadableStreamDefaultController<Uint8Array> | null;
  wsHandler: ((frame: Frame) => void) | null;
}

function makePending(): Pending {
  let resolveResponse!: (r: TranslatorResponse) => void;
  let rejectResponse!: (e: Error) => void;
  const responsePromise = new Promise<TranslatorResponse>((resolve, reject) => {
    resolveResponse = resolve;
    rejectResponse = reject;
  });
  return {
    responsePromise,
    resolveResponse,
    rejectResponse,
    bodyController: null,
    wsHandler: null,
  };
}

function createBodyStream(highWaterMark: number): {
  body: ReadableStream<Uint8Array>;
  bodyController: ReadableStreamDefaultController<Uint8Array>;
} {
  let controller!: ReadableStreamDefaultController<Uint8Array>;
  // Strategy with byte-length sizing so `desiredSize` measures the
  // queue in bytes. Web Streams doesn't propagate this back-pressure
  // to the producer automatically (we don't have a `pull` hook), so
  // the producer is expected to inspect `controller.desiredSize`
  // after enqueueing and act when it goes negative.
  const strategy: QueuingStrategy<Uint8Array> = {
    size: (chunk) => chunk.byteLength,
    highWaterMark,
  };
  const body = new ReadableStream<Uint8Array>(
    {
      start(c) {
        controller = c;
      },
    },
    strategy,
  );
  return { body, bodyController: controller };
}

function toWireHeaders(hs: Array<readonly [string, string]>): Headers {
  const enc = new TextEncoder();
  return hs.map(([n, v]) => [enc.encode(n.toLowerCase()), enc.encode(v)] as const);
}

function fromWireHeaders(hs: Headers): Array<readonly [string, string]> {
  const dec = new TextDecoder();
  return hs.map(([n, v]) => [dec.decode(n), dec.decode(v)] as const);
}

function tryDecode(buf: Uint8Array): ReturnType<typeof decode> {
  return decode(buf);
}
